import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Brain, Target, TrendingUp, Shield, Wallet, PieChart,
  Users, Zap, CheckCircle, Star, ChevronRight, Sparkles, Heart,
  BarChart3, CreditCard, Bell, RefreshCw, ArrowUpRight, Home,
  Plane, GraduationCap, Car, Gem, Gift, Flame, Trophy, Award,
} from 'lucide-react';
import { clsx } from 'clsx';

// ─── Data ────────────────────────────────────────────────────────────────────
const dreams = [
  { icon: Home, label: 'Casa Própria', color: 'from-blue-500 to-blue-700', value: 350000, years: 5 },
  { icon: Plane, label: 'Viagem dos Sonhos', color: 'from-purple-500 to-purple-700', value: 25000, years: 2 },
  { icon: GraduationCap, label: 'Faculdade dos Filhos', color: 'from-emerald-500 to-emerald-700', value: 120000, years: 10 },
  { icon: Car, label: 'Carro Novo', color: 'from-orange-500 to-orange-700', value: 80000, years: 3 },
  { icon: Gem, label: 'Aposentadoria', color: 'from-rose-500 to-rose-700', value: 1500000, years: 20 },
  { icon: Target, label: 'Meu Próprio Negócio', color: 'from-teal-500 to-teal-700', value: 200000, years: 7 },
];

const features = [
  {
    icon: Brain,
    title: 'Conselheiro com IA',
    description: 'Análise inteligente do seu perfil de consumo com recomendações personalizadas para melhorar suas finanças.',
    tag: 'IA',
    tagColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  },
  {
    icon: Target,
    title: 'Planejamento de Metas',
    description: 'Defina seus sonhos e receba um plano de economia detalhado baseado nos seus hábitos reais de consumo.',
    tag: 'Novo',
    tagColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  {
    icon: Wallet,
    title: 'Multi-contas',
    description: 'Integre todos seus bancos, cartões e investimentos em um painel unificado e sempre atualizado.',
  },
  {
    icon: BarChart3,
    title: 'Relatórios Profundos',
    description: 'Entenda onde seu dinheiro vai com gráficos detalhados por categoria, período e conta.',
  },
  {
    icon: CreditCard,
    title: 'Parcelamentos e Recorrências',
    description: 'Controle assinaturas, parcelas e despesas fixas. Nunca mais esqueça um vencimento.',
  },
  {
    icon: Zap,
    title: 'Simulador de Cenários',
    description: 'Teste decisões financeiras sem riscos: "e se eu trocasse de carro?" ou "e se eu aumentasse minha poupança?"',
  },
  {
    icon: TrendingUp,
    title: 'Portfólio de Investimentos',
    description: 'Acompanhe seus investimentos, calcule rentabilidade e simule projeções com juros compostos.',
  },
  {
    icon: Users,
    title: 'Finanças em Família',
    description: 'Compartilhe controle financeiro com até 5 pessoas. Cada um com seu nível de permissão.',
  },
  {
    icon: Bell,
    title: 'Alertas Inteligentes',
    description: 'Receba alertas antes de vencimentos, quando gastar muito em uma categoria ou saldo ficar baixo.',
  },
];

const testimonials = [];

const debtSteps = [
  { n: '01', title: 'Mapeamento Total', desc: 'Cadastre todas suas dívidas, juros e prazos. O MyBank vê o quadro completo.' },
  { n: '02', title: 'Análise dos Gastos', desc: 'IA identifica onde você está gastando mais do que deveria e quanto pode liberar.' },
  { n: '03', title: 'Plano de Ataque', desc: 'Receba uma ordem priorizada de quais dívidas pagar primeiro para economizar mais.' },
  { n: '04', title: 'Acompanhamento', desc: 'Alertas e relatórios semanais para manter o foco. Cada centavo conta.' },
];

const stats = [
  { value: 'Grátis', label: 'Para começar' },
  { value: 'IA', label: 'Conselheiro financeiro' },
  { value: 'Open', label: 'Finance integrado' },
  { value: '100%', label: 'Seus dados, seu controle' },
];

// ─── AnimatedCounter ──────────────────────────────────────────────────────────
const formatMoney = (n) => {
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(0)}k`;
  return `R$ ${n.toLocaleString('pt-BR')}`;
};

// ─── DreamCard ────────────────────────────────────────────────────────────────
const DreamCard = ({ dream, selected, onClick }) => {
  const Icon = dream.icon;
  return (
    <button
      onClick={onClick}
      className={clsx(
        'relative flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-200 text-center w-full',
        selected
          ? 'border-primary-500 shadow-lg shadow-primary-100 dark:shadow-primary-900/20 bg-primary-50 dark:bg-primary-900/20 scale-105'
          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 bg-white dark:bg-gray-800'
      )}
    >
      <div className={clsx('w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center', dream.color)}>
        <Icon size={22} className="text-white" />
      </div>
      <span className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{dream.label}</span>
      {selected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
          <CheckCircle size={12} className="text-white" />
        </div>
      )}
    </button>
  );
};

// ─── Landing ─────────────────────────────────────────────────────────────────
const Landing = () => {
  const [selectedDream, setSelectedDream] = useState(0);
  const [income, setIncome] = useState(5000);

  const dream = dreams[selectedDream];
  const monthlyNeeded = dream ? Math.ceil(dream.value / (dream.years * 12)) : 0;
  const feasible = monthlyNeeded <= income * 0.3;
  const savingPct = dream ? Math.min(100, ((monthlyNeeded / income) * 100)).toFixed(0) : 0;

  return (
    <div className="overflow-x-hidden">

      {/* ─── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-[90vh] flex items-center bg-gray-950 overflow-hidden">
        {/* Background gradients */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-primary-600/20 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-purple-600/15 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,#030712_100%)]" />
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '60px 60px' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-600/10 border border-primary-500/30 text-primary-400 text-sm font-medium mb-8">
              <Sparkles size={14} />
              Consultor financeiro com IA disponível 24h
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white leading-[1.1] mb-6">
              Mais que um app.
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-purple-400 bg-clip-text text-transparent">
                Seu conselheiro
              </span>
              <br />
              financeiro.
            </h1>

            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Analisamos seus hábitos de consumo, montamos um plano personalizado e ficamos ao seu lado
              até você realizar seus sonhos — ou sair das dívidas.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-500 transition-all shadow-lg shadow-primary-600/30 text-base"
              >
                Começar Grátis
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/features"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gray-700 text-gray-300 rounded-xl font-semibold hover:border-gray-500 hover:text-white transition-all text-base"
              >
                Ver como funciona
              </Link>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-2xl font-bold text-white">{s.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── DREAM PLANNER ────────────────────────────────────────────────── */}
      <section className="py-24 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-semibold mb-4">
              <Sparkles size={14} />
              Inteligência Artificial
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Defina seu sonho.
              <br />
              <span className="text-primary-600">Nós montamos o caminho.</span>
            </h2>
            <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
              Diga qual é o seu objetivo e informe sua renda. Nossa IA analisa quanto você precisa guardar
              por mês e cria um plano realista baseado no seu perfil.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Dream picker */}
            <div>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Qual é o seu sonho?</p>
              <div className="grid grid-cols-3 gap-3 mb-8">
                {dreams.map((dream, i) => (
                  <DreamCard
                    key={dream.label}
                    dream={dream}
                    selected={selectedDream === i}
                    onClick={() => setSelectedDream(i)}
                  />
                ))}
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 block mb-2">
                  Qual é sua renda mensal?
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 text-sm">R$ 1k</span>
                  <input
                    type="range"
                    min={1000}
                    max={30000}
                    step={500}
                    value={income}
                    onChange={(e) => setIncome(Number(e.target.value))}
                    className="flex-1 accent-primary-600"
                  />
                  <span className="text-gray-500 text-sm">R$ 30k</span>
                </div>
                <p className="text-center mt-2 font-bold text-primary-600">
                  R$ {income.toLocaleString('pt-BR')}/mês
                </p>
              </div>
            </div>

            {/* Right: Plan output */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 text-white shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className={clsx('w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center', dream?.color)}>
                  {dream && <dream.icon size={22} className="text-white" />}
                </div>
                <div>
                  <p className="text-sm text-gray-400">Seu sonho</p>
                  <p className="font-bold text-lg">{dream?.label}</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Valor estimado</span>
                  <span className="font-bold">R$ {dream?.value.toLocaleString('pt-BR')}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Prazo sugerido</span>
                  <span className="font-bold">{dream?.years} anos</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Guardar por mês</span>
                  <span className={clsx('font-bold text-lg', feasible ? 'text-emerald-400' : 'text-orange-400')}>
                    R$ {monthlyNeeded.toLocaleString('pt-BR')}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-400 text-sm">% da sua renda</span>
                  <span className={clsx('font-bold', feasible ? 'text-emerald-400' : 'text-orange-400')}>
                    {savingPct}%
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-6">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all duration-500', feasible ? 'bg-emerald-400' : 'bg-orange-400')}
                    style={{ width: `${Math.min(100, savingPct)}%` }}
                  />
                </div>
              </div>

              {feasible ? (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-sm">
                  <p className="text-emerald-400 font-semibold mb-1">Sonho alcançável!</p>
                  <p className="text-gray-300">Com {savingPct}% da sua renda guardados por mês, você alcança esse objetivo em {dream?.years} anos. Quer ver o plano completo?</p>
                </div>
              ) : (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-sm">
                  <p className="text-orange-400 font-semibold mb-1">Vamos ajustar o plano</p>
                  <p className="text-gray-300">Esse valor representa {savingPct}% da sua renda. Nossa IA vai analisar seus gastos e encontrar onde você pode economizar para tornar isso possível.</p>
                </div>
              )}

              <Link
                to="/register"
                className="mt-5 flex items-center justify-center gap-2 w-full py-3 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-semibold transition-colors"
              >
                Criar meu plano personalizado
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DEBT RESCUE ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-950 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-orange-600/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-semibold mb-6">
                <Heart size={14} />
                Modo Resgate
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Está nas dívidas?
                <br />
                <span className="text-red-400">A gente te tira.</span>
              </h2>
              <p className="text-xl text-gray-400 mb-10 leading-relaxed">
                Não julgamos. Entendemos que a vida acontece. Nossa IA analisa sua situação sem rodeios
                e cria um plano honesto e viável para você voltar ao azul.
              </p>

              <div className="space-y-5">
                {debtSteps.map((step) => (
                  <div key={step.n} className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 font-bold text-sm flex-shrink-0">
                      {step.n}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{step.title}</p>
                      <p className="text-gray-400 text-sm mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/register"
                className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold transition-colors"
              >
                Quero sair das dívidas
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="space-y-4">
              {/* Mock debt dashboard */}
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
                <p className="text-xs text-gray-500 uppercase font-semibold mb-4">Situação atual (exemplo)</p>
                <div className="space-y-3">
                  {[
                    { name: 'Cartão Nubank', value: 4200, rate: '8,9%/mês', color: 'bg-red-500' },
                    { name: 'Empréstimo pessoal', value: 12000, rate: '3,2%/mês', color: 'bg-orange-500' },
                    { name: 'Cartão Itaú', value: 1800, rate: '11,2%/mês', color: 'bg-red-600' },
                  ].map((debt) => (
                    <div key={debt.name} className="flex items-center justify-between p-3 bg-gray-800 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={clsx('w-2 h-8 rounded-full', debt.color)} />
                        <div>
                          <p className="text-sm font-medium text-white">{debt.name}</p>
                          <p className="text-xs text-gray-400">{debt.rate} de juros</p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-red-400">R$ {debt.value.toLocaleString('pt-BR')}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-800 flex justify-between">
                  <span className="text-sm text-gray-400">Total de dívidas</span>
                  <span className="font-bold text-red-400">R$ 18.000</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-900/40 to-emerald-800/20 rounded-2xl border border-emerald-700/30 p-6">
                <p className="text-xs text-emerald-400 uppercase font-semibold mb-3">Plano da IA</p>
                <p className="text-white font-semibold mb-1">Em 18 meses você pode quitar tudo</p>
                <p className="text-gray-400 text-sm mb-4">Guardando R$ 1.200/mês e cortando 3 gastos desnecessários identificados</p>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full w-2/5 bg-emerald-400 rounded-full" />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">Hoje</span>
                  <span className="text-xs text-emerald-400">Meta: 18 meses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Tudo em um só lugar
            </h2>
            <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
              Do básico ao avançado. Do iniciante ao investidor. O MyBank cresce com você.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group p-6 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-primary-400 dark:hover:border-primary-600 hover:shadow-lg transition-all duration-200 bg-white dark:bg-gray-900"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 bg-primary-50 dark:bg-primary-900/30 rounded-xl flex items-center justify-center text-primary-600 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50 transition-colors">
                      <Icon size={22} />
                    </div>
                    {f.tag && (
                      <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full', f.tagColor)}>
                        {f.tag}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{f.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:gap-3 transition-all"
            >
              Ver todos os recursos
              <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Simples de começar
            </h2>
            <p className="text-xl text-gray-500 dark:text-gray-400">Em minutos você já tem uma visão completa das suas finanças.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-12 left-[calc(12.5%+20px)] right-[calc(12.5%+20px)] h-0.5 bg-primary-200 dark:bg-primary-800 z-0" />

            {[
              { step: '1', title: 'Crie sua conta', desc: 'Gratuito, sem cartão de crédito. Em 2 minutos.' },
              { step: '2', title: 'Cadastre suas contas', desc: 'Bancos, cartões, carteiras. Tudo em um lugar.' },
              { step: '3', title: 'Registre seus gastos', desc: 'Manualmente ou via importação. É rápido.' },
              { step: '4', title: 'Deixe a IA trabalhar', desc: 'Análise, insights e plano personalizados para você.' },
            ].map((item) => (
              <div key={item.step} className="relative z-10 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-primary-600 text-white font-bold text-lg flex items-center justify-center shadow-lg shadow-primary-600/30 mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─────────────────────────────────────────────────── */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-white dark:bg-gray-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Vidas que mudaram
              </h2>
              <p className="text-xl text-gray-500 dark:text-gray-400">Histórias reais de quem usou o MyBank para transformar suas finanças.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-7 border border-gray-200 dark:border-gray-800">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-sm">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className={clsx('w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm', t.color)}>
                      {t.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── PRICING TEASER ───────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Comece de graça. Cresça quando quiser.
          </h2>
          <p className="text-xl text-gray-500 dark:text-gray-400 mb-10">
            Plano gratuito para sempre. Sem pegadinha. Sem cartão de crédito para começar.
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-10">
            {[
              { name: 'Free', price: 'Grátis', desc: 'Para começar', cta: 'Começar Agora', highlight: false },
              { name: 'Pro', price: 'R$ 19,90/mês', desc: 'Controle total + IA', cta: 'Assinar Pro', highlight: true },
              { name: 'Família', price: 'R$ 39,90/mês', desc: 'Até 5 pessoas', cta: 'Assinar Família', highlight: false },
            ].map((plan) => (
              <div key={plan.name} className={clsx(
                'rounded-2xl p-6 border-2',
                plan.highlight
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 scale-105 shadow-xl'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              )}>
                <p className="font-bold text-lg text-gray-900 dark:text-white">{plan.name}</p>
                <p className="text-2xl font-bold text-primary-600 mt-1 mb-1">{plan.price}</p>
                <p className="text-sm text-gray-500 mb-5">{plan.desc}</p>
                <Link to="/register" className={clsx(
                  'block py-2.5 rounded-xl text-sm font-semibold transition-colors',
                  plan.highlight
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                )}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          <Link to="/pricing" className="text-primary-600 font-medium hover:text-primary-700 transition-colors inline-flex items-center gap-1">
            Ver comparativo completo de planos
            <ChevronRight size={16} />
          </Link>
        </div>
      </section>

      {/* ─── GAMIFICATION & REFERRAL ─────────────────────────────────────── */}
      <section className="py-24 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Gamification */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 px-2.5 py-1 rounded-full">Novidade</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Suas financas viram um jogo
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                Ganhe XP, suba de nivel e desbloqueie conquistas enquanto organiza suas financas. Quanto mais voce usa, mais evolui.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Flame, text: 'Streaks diarios — mantenha o ritmo e ganhe XP bonus', color: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30' },
                  { icon: Trophy, text: '10 niveis de evolucao — de Iniciante a Lenda Financeira', color: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30' },
                  { icon: Award, text: '15 conquistas exclusivas para desbloquear', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
                  { icon: Star, text: 'Ranking entre membros do plano Familia', color: 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', item.color)}>
                      <item.icon size={18} />
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Referral */}
            <div>
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Gift size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Indique e Ganhe</h3>
                    <p className="text-sm text-white/70">Programa de indicacao em 3 niveis</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    { level: 'Nivel 1', desc: 'Indicado direto', rate: '9%' },
                    { level: 'Nivel 2', desc: 'Indicado do seu indicado', rate: '5%' },
                    { level: 'Nivel 3', desc: 'Terceiro nivel', rate: '1%' },
                  ].map((item) => (
                    <div key={item.level} className="bg-white/10 rounded-xl p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold">{item.level}</p>
                        <p className="text-xs text-white/60">{item.desc}</p>
                      </div>
                      <span className="text-2xl font-bold">{item.rate}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-white/10 rounded-xl p-4 text-center mb-5">
                  <p className="text-xs text-white/60 mb-1">Exemplo: 10 indicados diretos no plano Pro</p>
                  <p className="text-2xl font-bold">R$ 17,91<span className="text-sm font-normal text-white/60">/mes</span></p>
                </div>

                <p className="text-xs text-white/60 text-center">
                  Saque via Pix a partir de R$50,00. Comissoes recorrentes enquanto o indicado for assinante.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA FINAL ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[400px] h-[400px] bg-primary-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/3 w-[300px] h-[300px] bg-purple-600/15 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Seu futuro financeiro
            <br />
            começa hoje.
          </h2>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            Use inteligência artificial para realizar sonhos,
            quitar dívidas e construir patrimônio. Comece agora, é grátis.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-10 py-5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-bold text-lg transition-all shadow-xl shadow-primary-600/30"
          >
            Criar minha conta grátis
            <ArrowRight size={20} />
          </Link>
          <p className="text-gray-600 text-sm mt-4">Sem cartão de crédito. Cancele quando quiser.</p>
        </div>
      </section>
    </div>
  );
};

export default Landing;
